// File: components/AssemblerOverview.tsx

import React, { FC, useRef, useState } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Collapse,
  IconButton,
} from '@mui/material'
import {
  Cloud,
  LocalGasStation,
  Forest,
  EmojiNature,
  FlightTakeoff,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useReactToPrint } from 'react-to-print'
import PaperWrapper from 'components/custom/PaperWrapper'
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Pie,
  PieChart,
  Legend,
} from 'recharts'
import { EmptyData } from 'components/common/EmptyData'

interface AssemblerOverviewProps {
  currentAssemblyData: any
  currentAssemblyModel: any // Redux request state — holds detailed targets
  totalEmissions: number
  assemblyName: string
}

type AggregatedItem = {
  label: string
  amount: string
  emission: string
}

/** Filters resolved response targets by contextName substring. */
const getTargetsByContext = (targets: any[] | undefined, needle: string): any[] => {
  if (!targets?.length) return []
  return targets.filter(
    (t: any) =>
      typeof t.contextName === 'string' && t.contextName.includes(needle)
  )
}

/** Returns the top-level contextName group (e.g. "materials", "production"). */
const topContext = (target: any): string => {
  const raw = (target.contextName as string) || ''
  const parts = raw.replace(/^\./, '').split('.')
  return parts[0] ?? ''
}

/** Returns 'Materials', 'Production', etc. from target contextNames; 'Multiple Categories' if mixed. */
const deriveStepLabel = (targets: any[] | undefined): string => {
  if (!targets?.length) return '—'
  const contexts = [...new Set<string>(targets.map(topContext))]
  if (contexts.length > 1) return 'Multiple Categories'
  const single = contexts[0]
  return single.charAt(0).toUpperCase() + single.slice(1)
}

/** Calculate returns flat chain at data root; premade wraps as { result }. */
const unwrapChainResult = (data: any) => {
  if (!data) return null
  if (data.calculationProcessChain && data.totalEmissions != null) return data
  return data.result ?? null
}


export const AssemblerOverview: FC<AssemblerOverviewProps> = ({
  currentAssemblyData,
  currentAssemblyModel,
  totalEmissions,
  assemblyName,
}) => {
  const { t } = useTranslation()

  // Image upload state
  const [imageUrl, setImageUrl] = useState<string | null>(
    currentAssemblyData?.result?.imageUrl || null
  )
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const componentRef = useRef<HTMLDivElement>(null)
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: 'AssemblerSummary',
  })

  // Handler for uploading or changing image
  const handleImageUpload = () => {
    const url = window.prompt('Enter image URL:')
    if (url) {
      setImageUrl(url)
      // optionally: persist to backend
    }
  }

  if (!currentAssemblyData || currentAssemblyData.length === 0)
    return <EmptyData />

  const chainResult = unwrapChainResult(currentAssemblyData)
  if (!chainResult) return <EmptyData />

  const assemblyId = currentAssemblyData?.result?.assemblyId || currentAssemblyData?.assemblyId || 'Internal ID'
  const standard = 'ISO 14067:2018, GHG Protocol'
  const systemBoundaries = 'Cradle to Gate'
  const databaseVersion = 'sustamize v3.1.0'
  // Emissions breakdown
  const fossilValue =
    chainResult?.emissionOriginComposition?.fossil ?? 0
  const lulucValue =
    chainResult?.emissionOriginComposition?.luluc ?? 0
  const biogenicValue =
    chainResult?.emissionOriginComposition?.biogenic ?? 0
  const airplaneValue =
    chainResult?.emissionOriginComposition?.aircraft ?? 0

  // Category breakdown (vertical)
  const categoryData = [
    { name: 'Fossil', value: fossilValue },
    { name: 'LULUC', value: lulucValue },
    { name: 'Biogenic', value: biogenicValue },
  ]
  const categoryColors = ['#4caf50', '#ff9800', '#2196f3']

  return (
    <PaperWrapper>
      <Box ref={componentRef} sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t('assembler.summary')}
        </Typography>

        {/* Table of Name/ID/etc. */}
        <Table size="small" sx={{ mb: 3 }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
              <TableCell>{assemblyName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
              <TableCell>{assemblyId}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Image</TableCell>
              <TableCell>
                {imageUrl ? (
                  <Box
                    component="img"
                    src={imageUrl}
                    alt="Assembly"
                    sx={{
                      width: 200,
                      height: 120,
                      objectFit: 'contain',
                      borderRadius: '4px',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      border: '2px dashed #fa951d',
                      color: '#fa951d',
                      width: 200,
                      height: 120,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      ':hover': {
                        backgroundColor: 'rgba(250,149,29, 0.1)',
                      },
                    }}
                    onClick={handleImageUpload}
                  >
                    + Upload Image
                  </Box>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>
                Accounting Standard
              </TableCell>
              <TableCell>{standard}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>
                System Boundaries
              </TableCell>
              <TableCell>{systemBoundaries}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>
                Database Version
              </TableCell>
              <TableCell>{databaseVersion}</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* 
          Row with 4 segments:
          (A) Category chart (220x220)
          (B) Fuel breakdown table
          (C) Scope breakdown chart (220x220 vertical)
          (D) Scope breakdown table
        */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center', // center horizontally
            alignItems: 'flex-start',
            gap: 3,
            flexWrap: 'nowrap',
            // wrap on print so content isn't clipped
            '@media print': {
              flexWrap: 'wrap',
              overflow: 'visible',
            },
          }}
        >
          {/* (A) Category Breakdown */}
          <Box sx={{ width: 280, height: 220, flex: '0 0 auto' }}>
            <Typography variant="h6" gutterBottom>
              Category Breakdown
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={categoryData}
                margin={{ top: 10, right: 15, left: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis
                  label={{
                    value: 'kgCO2e',
                    angle: -90,
                    position: 'insideLeft',
                    offset: -15,
                  }}
                />
                <Tooltip />
                <Bar dataKey="value">
                  {categoryData.map((entry, idx) => (
                    <Cell
                      key={`catCell-${idx}`}
                      fill={categoryColors[idx] ?? '#8884d8'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {/* (B) Fuel / LULUC / Biogenic / Airplane + total table */}
          <Box sx={{ minWidth: 280, flex: '0 0 auto' }}>
            <Typography variant="h6" gutterBottom>
              Emissions Breakdown
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto auto',
                columnGap: 2,
                borderBottom: '1px solid #ccc',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Cloud fontSize="medium" sx={{ mr: 1 }} />
                <Typography variant="body1" fontWeight="bold" align="left">
                  Total
                </Typography>
              </Box>

              <Typography variant="body1" fontWeight="bold" align="right">
                {totalEmissions.toFixed(5)} kgCO2e
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto auto',
                columnGap: 2,
                rowGap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocalGasStation fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2">Fossil</Typography>
              </Box>
              <Typography variant="body2" align="right">
                {fossilValue.toFixed(5)}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Forest fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2">LULUC</Typography>
              </Box>
              <Typography variant="body2" align="right">
                {lulucValue.toFixed(5)}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmojiNature fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2">Biogenic</Typography>
              </Box>
              <Typography variant="body2" align="right">
                {biogenicValue.toFixed(5)}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FlightTakeoff fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2">Airplane</Typography>
              </Box>
              <Typography variant="body2" align="right">
                {airplaneValue.toFixed(5)}
              </Typography>
            </Box>
          </Box>
        </Box>
        {/* New Row: Step Breakdown + Donut Chart */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 4,
            mt: 6,
            flexWrap: 'wrap',
          }}
        >
          {/* Left: Step Breakdown */}
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Typography variant="h6" gutterBottom>
              Calculation Step Breakdown
            </Typography>

            {(chainResult?.calculationProcessChain ?? []).map(
              (responseStep: any, idx: number) => {
                const isOpen = expandedIndex === idx

                const percent = (
                  ((responseStep.totalEmissions ?? 0) / (totalEmissions || 1)) * 100
                ).toFixed(2)

                const stepTargets = responseStep.targets ?? []
                const stepLabel = deriveStepLabel(stepTargets)

                const allMaterialSubtypes = [
                  'metals', 'polymers', 'elements', 'composites',
                  'sinters', 'chemicals', 'other',
                ]
                const productionSubtypes = [
                  'energy', 'manufacturing', 'employees', 'equipment',
                ]

                const aggregatedMaterials: AggregatedItem[] =
                  allMaterialSubtypes.flatMap((sub) =>
                    getTargetsByContext(stepTargets, sub).map(
                      (t: any) => ({
                        label: t.customName || t.name || t.contextName,
                        amount: `${(
                          t.calculationParameters?.netWeight ?? 0
                        ).toFixed(5)} kg`,
                        emission: `${(t.emissions ?? 0).toFixed(5)} kgCO₂e`,
                      })
                    )
                  )

                const aggregatedProduction: AggregatedItem[] =
                  productionSubtypes.flatMap((sub) =>
                    getTargetsByContext(stepTargets, sub).map(
                      (t: any) => ({
                        label: t.customName || t.name || t.contextName,
                        amount:
                          sub === 'energy'
                            ? `${(
                                t.calculationParameters?.consumption ?? 0
                              ).toFixed(5)} kWh`
                            : '1 unit',
                        emission: `${(t.emissions ?? 0).toFixed(5)} kgCO₂e`,
                      })
                    )
                  )

                const aggregatedLogistics: AggregatedItem[] = [
                  ...getTargetsByContext(stepTargets, 'transportation').map(
                    (t: any) => ({
                      label: t.customName || t.name || t.contextName,
                      amount: `${(
                        t.calculationParameters?.distance ?? 0
                      ).toFixed(5)} tkm`,
                      emission: `${(t.emissions ?? 0).toFixed(5)} kgCO₂e`,
                    })
                  ),
                  ...getTargetsByContext(stepTargets, 'packaging').map(
                    (t: any) => ({
                      label: t.customName || t.name || t.contextName,
                      amount: `${(
                        t.calculationParameters?.netWeight ?? 0
                      ).toFixed(5)} kg`,
                      emission: `${(t.emissions ?? 0).toFixed(5)} kgCO₂e`,
                    })
                  ),
                ]

                const aggregatedComponents: AggregatedItem[] =
                  getTargetsByContext(stepTargets, 'electronics').map((t: any) => ({
                    label: t.customName || t.name || t.contextName,
                    amount: `${(
                      t.calculationParameters?.quantity ?? 0
                    ).toFixed(0)} pcs`,
                    emission: `${(t.emissions ?? 0).toFixed(5)} kgCO₂e`,
                  }))

                const renderCategory = (
                  title: string,
                  data: AggregatedItem[],
                  keyPrefix: string
                ) => {
                  if (data.length === 0) return null
                  return (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        {title}
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 120px 120px',
                          columnGap: 3,
                          borderBottom: '1px solid #ddd',
                          pb: 0.5,
                          mb: 1,
                        }}
                      >
                        <Typography variant="caption" fontWeight="bold">Name</Typography>
                        <Typography variant="caption" fontWeight="bold">Amount</Typography>
                        <Typography variant="caption" fontWeight="bold">kgCO₂e</Typography>
                      </Box>
                      {data.map((item, i) => (
                        <Box
                          key={`${keyPrefix}-${i}`}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 120px 120px',
                            columnGap: 3,
                            py: 0.5,
                            borderBottom: '1px solid #eee',
                          }}
                        >
                          <Typography variant="body2">{item.label}</Typography>
                          <Typography variant="body2">{item.amount}</Typography>
                          <Typography variant="body2">{item.emission}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )
                }

                return (
                  <Box
                    key={responseStep.name + idx}
                    sx={{
                      border: '1px solid #eee',
                      borderRadius: 2,
                      mb: 2,
                      backgroundColor: '#fafafa',
                    }}
                  >
                    {/* Header Row */}
                    <Box
                      onClick={() =>
                        setExpandedIndex((prev) => (prev === idx ? null : idx))
                      }
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        cursor: 'pointer',
                      }}
                    >
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {responseStep.name}
                        </Typography>
                        {/* Label derived from Redux targets' contextName prefixes */}
                        <Typography variant="caption" color="text.secondary">
                          {stepLabel}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {percent}% of total
                        </Typography>
                        <IconButton size="small">
                          {isOpen ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Collapsible Content */}
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 3, pb: 3 }}>
                        {/* ── Step documentation (from response) */}
                        {responseStep.comment && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Comment
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ whiteSpace: 'pre-line' }}
                            >
                              {responseStep.comment}
                            </Typography>
                          </Box>
                        )}

                        {/* ── Material / Production / Logistics / Components tables */}
                        {renderCategory('Materials', aggregatedMaterials, 'mat')}
                        {renderCategory('Production', aggregatedProduction, 'prod')}
                        {renderCategory('Logistics', aggregatedLogistics, 'log')}
                        {renderCategory('Components', aggregatedComponents, 'comp')}

                        {/* ── Origin composition (from response) */}
                        {responseStep.emissionOriginComposition && (
                          <>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Origin Composition
                            </Typography>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                                gap: 1,
                              }}
                            >
                              {Object.entries(responseStep.emissionOriginComposition).map(
                                ([key, val]: [string, any]) => (
                                  <Box key={key} sx={{ textAlign: 'center' }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      {key.charAt(0).toUpperCase() + key.slice(1)}
                                    </Typography>
                                    <Typography variant="body2">
                                      {Number(val).toFixed(5)}
                                    </Typography>
                                  </Box>
                                )
                              )}
                            </Box>
                          </>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                )
              }
            )}
          </Box>

          {/* Right: Donut Chart */}
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Typography variant="h6" gutterBottom>
              LCA Impact Breakdown
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  dataKey="value"
                  data={[
                    {
                      name: 'Materials',
                      value: chainResult?.emissionsByCategory?.materials ?? 0,
                    },
                    {
                      name: 'Production',
                      value: chainResult?.emissionsByCategory?.production ?? 0,
                    },
                    {
                      name: 'Logistics',
                      value: chainResult?.emissionsByCategory?.logistics ?? 0,
                    },
                    {
                      name: 'Components',
                      value: chainResult?.emissionsByCategory?.components ?? 0,
                    },
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={1}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(1)}%`
                  }
                >
                  <Cell fill="#4caf50" /> {/* Materials */}
                  <Cell fill="#2196f3" /> {/* Production */}
                  <Cell fill="#ff9800" /> {/* Logistics */}
                  <Cell fill="#9c27b0" /> {/* Components */}
                </Pie>
                <Tooltip
                  formatter={(value: number) => `${value.toFixed(5)} kgCO₂e`}
                />
                <Legend
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                  iconType="circle"
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>

      {/* Export PDF button (hidden when printing) */}
      <Box sx={{ mt: 2, '@media print': { display: 'none' } }}>
        <Button variant="contained" onClick={() => handlePrint()}>
          Export PDF
        </Button>
      </Box>
    </PaperWrapper>
  )
}
